import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera } from "lucide-react";

/**
 * /anzeige/externenpruefung
 * Drei Editorial-Poster für die Externenprüfung Pflegefachassistenz (NRW).
 * Zielgruppe: erfahrene Pflegehilfskräfte — "Du machst den Job seit Jahren.
 * Jetzt mach ihn offiziell." Eigene Fotoserie, 4:5 für Meta Feed.
 */

const LOGO = "/dh_logo_transparent.png";
const IMG_MORNING = "/ads/epfa_morning.png";
const IMG_STUDY   = "/ads/epfa_evening_study.png";
const IMG_PROUD   = "/ads/epfa_proud.png";

export default function AnzeigeExternenpruefung() {
  const [variant, setVariant] = useState("all");
  return (
    <div className="min-h-screen bg-espresso-deep text-cream-50">
      <div className="border-b border-white/10 bg-espresso-deep/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            data-testid="anzeige-epfa-back"
            className="inline-flex items-center gap-1.5 text-[13px] text-cream-100 hover:text-saffron"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> zurück
          </Link>
          <div className="flex items-center gap-1.5 flex-wrap">
            <VariantBtn active={variant === "all"} onClick={() => setVariant("all")} label="Alle" />
            <VariantBtn active={variant === "v1"} onClick={() => setVariant("v1")} label="V1 · Morgen" />
            <VariantBtn active={variant === "v2"} onClick={() => setVariant("v2")} label="V2 · Abendkurs" />
            <VariantBtn active={variant === "v3"} onClick={() => setVariant("v3")} label="V3 · Stolz" />
          </div>
          <div className="text-[11px] text-cream-100/60 inline-flex items-center gap-1">
            <Camera className="h-3 w-3" /> 4:5 · Screenshot für Ad-Manager
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center rounded-full border border-white/20 text-cream-100/80 text-[10px] font-bold tracking-widest uppercase px-3 py-1 mb-3">
              Externenprüfung Pflegefachassistenz · NRW · kein Stock
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight">
              Deine Erfahrung. <span className="text-saffron">Endlich mit Abschluss.</span>
            </h1>
          </div>

          <div className={`grid ${variant === "all" ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 justify-items-center"} gap-6 sm:gap-8 items-start`}>
            {(variant === "all" || variant === "v1") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V1" name="Frühschicht · Morgen" />
                <V1Morgen />
              </div>
            )}
            {(variant === "all" || variant === "v2") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V2" name="Abendkurs · Testimonial" />
                <V2Abendkurs />
              </div>
            )}
            {(variant === "all" || variant === "v3") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V3" name="Stolz · Full Bleed" />
                <V3Stolz />
              </div>
            )}
          </div>

          {/* Verkaufsclip · Editorial Kinetic · 9:16 */}
          <div className="mt-14 sm:mt-20 text-center">
            <div className="inline-flex items-center rounded-full border border-white/20 text-cream-100/80 text-[10px] font-bold tracking-widest uppercase px-3 py-1 mb-3">
              Verkaufsclip · 9:16 · 17 Sek · für Reels &amp; Meta-Ads
            </div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-8">
              Deine Fotoserie. <span className="text-saffron">Als Film.</span>
            </h2>
            <div className="max-w-[380px] mx-auto">
              <video
                src="/ads/epfa_verkaufsclip.mp4"
                poster="/ads/epfa_verkaufsclip_poster.jpg"
                data-testid="epfa-verkaufsclip"
                className="w-full aspect-[9/16] rounded-2xl object-cover shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] border border-white/10"
                controls
                muted
                playsInline
                preload="metadata"
              />
              <a
                href="/ads/epfa_verkaufsclip.mp4"
                download="dickies-helden-externenpruefung-clip.mp4"
                data-testid="epfa-verkaufsclip-download"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-saffron text-espresso-deep px-5 py-2.5 text-[13px] font-black hover:opacity-90 transition-opacity"
              >
                Clip herunterladen (MP4 · 1080×1920)
              </a>
              <p className="mt-3 text-[11.5px] text-cream-100/60">
                Ohne Ton produziert — im Meta Ads Manager einfach einen Trend-Sound drüberlegen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V1 · MORGEN — Foto oben (58%), Cream unten.
 * Emotion: Sie macht den Job seit Jahren — jetzt wird's offiziell.
 * ================================================================ */
function V1Morgen() {
  return (
    <div
      data-testid="ad-epfa-v1"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-cream-50 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] flex flex-col group"
    >
      <div className="relative h-[58%] overflow-hidden">
        <img
          src={IMG_MORNING}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
          draggable={false}
        />
        <div className="absolute inset-0 bg-terra/10 mix-blend-multiply pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.09] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }}
        />

        <div className="absolute top-4 left-5 right-5 flex items-center justify-between text-[9.5px] uppercase tracking-[0.28em] font-bold">
          <span className="inline-flex items-center gap-2">
            <DhLogoChip />
            <span className="text-cream-50 mix-blend-difference">Dickies-Campus · Nº 09</span>
          </span>
          <span className="text-cream-50 mix-blend-difference">MMXXVI</span>
        </div>

        <div className="absolute bottom-3 left-5 right-5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-cream-50 bg-espresso-deep/85 rounded-full px-2.5 py-1">
            für Pflegehilfskräfte
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.20em] text-espresso-deep bg-saffron rounded-full px-2.5 py-1">
            Prüfungsplatz über Partnerschulen
          </span>
        </div>
      </div>

      <div className="flex-1 relative px-6 pt-4 pb-5 flex flex-col">
        <div className="absolute -top-[1px] left-6 h-[3px] w-14 bg-terra" />

        <div className="flex items-center gap-2 mb-2">
          <PflegeMark />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.24em] text-terra">
            Externenprüfung Pflegefachassistenz · NRW
          </span>
        </div>

        <h2 className="font-display text-espresso-deep font-black tracking-[-0.02em] leading-[0.95] text-[30px] mb-2">
          Du machst den Job <br/>
          <span className="italic text-terra">seit Jahren. Jetzt offiziell.</span>
        </h2>
        <p className="text-[12px] leading-[1.45] text-sand-700 max-w-[310px] mb-3">
          Ohne die Ausbildung neu zu beginnen: <strong className="text-espresso-deep">berufsbegleitend und in deinem Tempo</strong>{" "}
          zur staatlich anerkannten Pflegefachassistenz — mehr Verantwortung, besseres Gehalt.
        </p>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-espresso-deep text-cream-50 px-4 py-2 text-[12px] font-bold">
            Jetzt Platz sichern
            <span className="inline-block w-1 h-1 rounded-full bg-saffron" />
          </div>
          <div className="text-right leading-tight">
            <div className="text-[8.5px] uppercase tracking-[0.24em] font-bold text-espresso-deep/60">Start jeden Montag</div>
            <div className="font-display text-[20px] leading-none font-black text-brand-darkred">
              € 2.490
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V2 · ABENDKURS — Split. Oben Lern-Still-Life am Abend,
 * unten Terra mit Testimonial einer Absolventin.
 * ================================================================ */
function V2Abendkurs() {
  return (
    <div
      data-testid="ad-epfa-v2"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] flex flex-col"
    >
      <div className="relative h-[50%] overflow-hidden">
        <img src={IMG_STUDY} alt="" className="w-full h-full object-cover" draggable={false} />
        <div className="absolute inset-0 bg-espresso-deep/10 mix-blend-multiply pointer-events-none" />

        <div className="absolute top-4 left-5 inline-flex items-center gap-1.5 rounded-full bg-cream-50/95 text-espresso-deep pl-1 pr-2.5 py-1 shadow">
          <img src={LOGO} alt="Dickies-Campus" className="h-4 w-4 object-contain" draggable={false} />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.24em]">
            Abend- oder Vormittagskurs
          </span>
        </div>

        <div className="absolute bottom-3 right-5 text-[9px] font-bold uppercase tracking-[0.32em] text-cream-50 bg-espresso-deep/70 rounded-full px-2.5 py-1">
          Nº 09 · MMXXVI
        </div>
      </div>

      <div className="relative flex-1 bg-terra text-cream-50 px-6 pt-5 pb-5 flex flex-col">
        <div className="absolute -top-[1px] left-0 right-0 h-[1px] bg-cream-50/40" />
        <div className="absolute top-2 left-5 font-display text-[80px] leading-none text-saffron/40 select-none">
          „
        </div>

        <blockquote className="pl-8 pr-3 font-display italic text-[16px] leading-[1.15] text-cream-50">
          Acht Jahre Pflegehilfe — und keiner hat mir gesagt, dass ich die Prüfung machen kann, ohne die Ausbildung neu anzufangen. Heute habe ich meinen Abschluss in der Tasche.
        </blockquote>

        <div className="pl-8 mt-2 text-[10.5px] uppercase tracking-[0.24em] font-bold text-cream-50/80">
          — Sandra B., Pflegefachassistentin
        </div>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="leading-tight">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-cream-50 shadow">
                <img src={LOGO} alt="" className="h-3.5 w-3.5 object-contain" draggable={false} />
              </span>
              <span className="text-[9px] uppercase tracking-[0.28em] font-bold text-cream-50/70">
                Externenprüfung · 300 UE · NRW
              </span>
            </div>
            <div className="font-display font-black text-[20px] leading-none tracking-tight">
              Start jeden Montag
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-cream-50 text-espresso-deep px-4 py-2 text-[12px] font-bold shadow whitespace-nowrap shrink-0">
            Platz sichern
            <span className="inline-block w-1 h-1 rounded-full bg-terra" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V3 · STOLZ — Full-Bleed. Der Moment mit dem Briefumschlag.
 * Emotion: Jahre der Arbeit — endlich anerkannt.
 * ================================================================ */
function V3Stolz() {
  return (
    <div
      data-testid="ad-epfa-v3"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] bg-espresso-deep group"
    >
      <img
        src={IMG_PROUD}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
        draggable={false}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-espresso-deep/40 via-transparent to-espresso-deep/95" />
      <div className="absolute inset-0 bg-terra/8 mix-blend-multiply pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.12] pointer-events-none mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")" }}
      />

      <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-[9.5px] uppercase tracking-[0.28em] font-bold text-cream-50/85">
        <span className="inline-flex items-center gap-2">
          <DhLogoChip />
          <span>Dickies-Campus · Nº 09</span>
        </span>
        <span className="text-saffron">MMXXVI</span>
      </div>

      <div className="absolute top-14 left-5">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron text-espresso-deep px-3 py-1.5 shadow-lg">
          <PflegeMark />
          <span className="text-[10px] font-black uppercase tracking-[0.22em]">
            Prüfungsplatz über Partnerschulen · NRW
          </span>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-6 pb-6">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-espresso-deep/80 backdrop-blur-sm text-saffron px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.24em] mb-3">
          Externenprüfung Pflegefachassistenz · berufsbegleitend
        </div>

        <h2 className="font-display font-black tracking-[-0.03em] leading-[0.86] text-cream-50 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
          <span className="block text-[38px]">Deine Erfahrung.</span>
          <span className="block text-[38px] italic">Endlich mit</span>
          <span className="block text-[38px] underline decoration-[3px] decoration-saffron underline-offset-[8px]">Abschluss.</span>
        </h2>

        <p className="mt-3 max-w-[290px] text-[12.5px] text-cream-100/85 leading-relaxed">
          <strong className="text-cream-50">300 UE, vormittags oder abends</strong> — neben deinem Dienst.{" "}
          <span className="text-saffron font-black">Start jeden Montag.</span>
        </p>

        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron text-espresso-deep px-4 py-2 text-[12px] font-black shadow">
            Jetzt Platz sichern
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-darkred" />
          </div>
          <div className="text-right leading-tight">
            <div className="text-[8px] uppercase tracking-[0.28em] font-bold text-cream-50/60">Web</div>
            <div className="text-[10.5px] font-black tracking-tight text-cream-50">dickies-helden.de</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* DH-Logo in kleinem Cream-Chip · funktioniert auf Foto & dunklem Grund. */
function DhLogoChip() {
  return (
    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-cream-50/95 shadow-md">
      <img src={LOGO} alt="Dickies-Campus" className="h-4 w-4 object-contain" draggable={false} />
    </span>
  );
}

/* Pflege-Kreuz-Mark · signalisiert "Pflege" ohne Klischee. */
function PflegeMark({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className="shrink-0" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="currentColor" opacity="0.18" />
      <path d="M10 5h4v5h5v4h-5v5h-4v-5H5v-4h5V5z" fill="currentColor" />
    </svg>
  );
}

function PosterLabel({ n, name }) {
  return (
    <div className="mb-3 flex items-center gap-2 text-cream-50">
      <span className="inline-flex items-center justify-center h-7 min-w-7 px-2 rounded-full bg-saffron text-espresso-deep font-display font-black text-[13px]">
        {n}
      </span>
      <span className="font-display text-lg">{name}</span>
    </div>
  );
}

function VariantBtn({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-[12px] font-semibold transition-colors ${
        active
          ? "bg-saffron text-espresso-deep"
          : "border border-white/20 text-cream-100/80 hover:border-saffron/60"
      }`}
    >
      {label}
    </button>
  );
}
